import { TRANSLATIONS_DISABLE_REASON_INCOMPLETE_TRANSCODING, QNA_LIMIT_ERRORS } from '../constants';
import * as finalCopy from '../copy/final';

/**
 * A hodge podge of utility functions.
 */

export const updateThumbnailUri = (thumbnailUri: unknown): string => {
  // Return default if we don't have a value from API:
  if (typeof thumbnailUri !== 'string') {
    return 'https://devi.vimeocdn.com/video/default_80x48';
  }
  // Resize the thumbnail to be 48px tall:
  return thumbnailUri.replace(/h=\d+/, 'h=48');
};

/**
 * The MM:SS representation of the seconds passed in
 * @param seconds
 */
export const secondsToDisplay = (seconds: number): string => {
  const hourInSeconds = 3600;
  const date = new Date(seconds * 1000).toISOString();

  if (seconds >= hourInSeconds) {
    return date.substr(11, 8);
  }

  return date.substr(14, 5);
};

/**
 * Get version id from version resource uri (i.e - /videos/123/versions/456 => 456)
 * @param {String} uri - resource uri
 */
export const versionIdFromUri = (uri: string | undefined): number => {
  const tokens = uri?.split('/') || [];
  const versionId = tokens[tokens.length - 1];
  return parseInt(versionId, 10);
};

/**
 * Get video id from video resource uri (i.e - /videos/123 => "123")
 * @param {String} uri - resource uri
 */
export const videoIdFromUri = (uri?: string): string | undefined => {
  if (!uri) return undefined;
  const match = uri.match(/\/videos\/(\d+)/);
  return match?.[1];
};

/**
 * Used because Chapter titles have to be 50 characters or fewer
 */
export const shortenStringToFiftyChars = (str: string): string => {
  return str.length > 50 ? str.substring(0, 50) : str;
};

/**
 * API error responses are converted to `NetworkError` objects if the `res.ok` is false.
 * As a result, we don't get access to the response body with helpful error messages.
 * This function helps us re-type the error object as something with a Response body that we can parse.
 */
export const isErrorWithResponse = (err: unknown): err is { res: Response } => {
  return typeof err === 'object' && !!err && (err as any)?.['res'] instanceof Response;
};

/**
 * Checks if we can parse the error object's response body for a helpful error message.
 * If yes (and it's a string), then we notify the user with the error message.
 * `notify` is an optional function that displays a toast notification.
 */
export async function handleError(
  err: unknown,
  mainMessage: string,
  notify?: (message: string) => void,
) {
  if (!isErrorWithResponse(err)) return;
  const formattedError = await err?.res.clone().json();
  if (typeof formattedError?.error === 'string') {
    notify?.(`${mainMessage}. ${formattedError.error}`);
  } else {
    notify?.(mainMessage);
  }
}

export const getDeviceType = (): 'tablet' | 'mobile' | 'desktop' => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return 'tablet';
  if (
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua,
    )
  )
    return 'mobile';
  return 'desktop';
};

/**
 * Copies a content to the clipboard
 * @param content
 */
export function copyToClipBoard(content: string) {
  return navigator.clipboard.writeText(content);
}

export async function isClipInterractionsLimitError(error: unknown) {
  if (!isErrorWithResponse(error)) return false;

  try {
    const parsedError = await error.res.clone().json();

    return QNA_LIMIT_ERRORS.includes(parsedError.error_code);
  } catch {
    return false;
  }
}

export function getDisabledTooltipCopy(reason: string, feature: string = '') {
  switch (reason) {
    case `transcript_status_language_not_supported`:
    case `transcript_status_no_speech`:
    case `transcript_status_failed`:
    case `transcript_status_unknown`:
    case `transcript_status_exceeds_maximum_duration`:
    case `transcript_status_does_not_exist`:
      return finalCopy.transcriptNotDetected;

    case `video_not_ready`:
    case `transcript_status_not_started`:
    case `transcript_status_in_progress`:
      return finalCopy.unavailableUntilTranscriptIsReady;

    case `video_too_short`:
    case `video_transcript_too_short`:
      return finalCopy.transcriptIsTooShort;

    case `video_resolution_too_low`:
      return finalCopy.unsupportedResolutionForHighlights;

    case TRANSLATIONS_DISABLE_REASON_INCOMPLETE_TRANSCODING:
      return finalCopy.translationsUnavailableWhileTranscoding;

    case `activation_processing`:
      return finalCopy.activationInProgress;

    case `transcript_ready_but_not_english`:
      return finalCopy.unavailableForThisVideosLanguage;

    case `owner_disabled_feature_group`:
      return finalCopy.disabledFeatureGroupTooltip(feature);

    case `feature_disabled`:
    default:
      return finalCopy.disabled;
  }
}

// Transcript-based disable reasons that don't apply to multimodal (VIS) users.
// Multimodal uses visual+audio analysis, not transcript, so transcript
// missing/failed/short/unsupported should not block Video Details.
// AI-8418-CLEANUP: Remove when backend SummaryInteractionResponse stops returning
// transcript-based reasons for multimodal users.
// Confirmed by ML team: VIS has no minimum video length requirement.
// Note: transcript_status_in_progress, transcript_status_not_started, and
// transcript_status_unknown are intentionally NOT in this list — VIS activation
// is triggered after transcription completes, so these should still block.
const TRANSCRIPT_REASONS_TO_SKIP_FOR_MULTIMODAL = [
  'transcript_status_does_not_exist',
  'transcript_status_no_speech',
  'transcript_status_failed',
  'transcript_status_language_not_supported',
  'transcript_status_exceeds_maximum_duration',
  'video_transcript_too_short',
  'video_too_short',
  'transcript_ready_but_not_english',
];

const isTranscriptReasonSkippableForMultimodal = (
  canUseMultiModalMetadata: boolean,
  summaryDisabledReason?: string | null,
): boolean =>
  canUseMultiModalMetadata &&
  !!summaryDisabledReason &&
  TRANSCRIPT_REASONS_TO_SKIP_FOR_MULTIMODAL.includes(summaryDisabledReason);

type MetadataActionDisabledArgs = {
  canUseMultiModalMetadata: boolean;
  summaryDisabled?: boolean;
  summaryDisabledReason?: string | null;
  isTranscriptingLoading?: boolean;
};

export const isMetadataActionDisabled = ({
  canUseMultiModalMetadata,
  summaryDisabled = false,
  summaryDisabledReason,
  isTranscriptingLoading = false,
}: MetadataActionDisabledArgs): boolean => {
  if (
    summaryDisabled &&
    !isTranscriptReasonSkippableForMultimodal(canUseMultiModalMetadata, summaryDisabledReason)
  ) {
    return true;
  }

  return canUseMultiModalMetadata && isTranscriptingLoading;
};

type MetadataActionDisabledReasonArgs = MetadataActionDisabledArgs & {
  isActivationInProgress?: boolean;
  activationProcessingReason: string;
  transcriptionInProgressReason: string;
  fallbackReason: string;
};

export const getMetadataActionDisabledReason = ({
  canUseMultiModalMetadata,
  summaryDisabled = false,
  summaryDisabledReason,
  isActivationInProgress = false,
  isTranscriptingLoading = false,
  activationProcessingReason,
  transcriptionInProgressReason,
  fallbackReason,
}: MetadataActionDisabledReasonArgs): string => {
  if (
    summaryDisabled &&
    summaryDisabledReason &&
    !isTranscriptReasonSkippableForMultimodal(canUseMultiModalMetadata, summaryDisabledReason)
  ) {
    return summaryDisabledReason;
  }

  if (!canUseMultiModalMetadata) {
    return summaryDisabledReason ?? fallbackReason;
  }

  if (isActivationInProgress) {
    return activationProcessingReason;
  }

  if (isTranscriptingLoading) {
    return transcriptionInProgressReason;
  }

  return fallbackReason;
};

/**
 * Get video request id from video id and video hash.
 */
export const getVideoRequestId = (
  videoId: string | number,
  videoHash: string | undefined,
): string => {
  return videoHash ? `${videoId}:${videoHash}` : videoId.toString();
};
